import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Ban, CircleCheck, Gift } from "lucide-react";
import { toast } from "sonner";

const SUB_META = {
  active: { label: "Actif", cls: "border-green-500 text-green-600" },
  paid: { label: "Payant", cls: "border-green-500 text-green-600" },
  trial: { label: "Essai", cls: "border-amber-500 text-amber-600" },
  expired: { label: "Expiré", cls: "border-destructive text-destructive" },
};

export default function AdminCompanies() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [gift, setGift] = useState(null); // company being gifted
  const [months, setMonths] = useState(1);
  const [suspendTarget, setSuspendTarget] = useState(null); // company being suspended
  const [reason, setReason] = useState("");

  const load = () => api.get("/admin/companies").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((c) =>
      (c.company_name || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s));
  }, [items, q]);

  const sub = (c) => SUB_META[c.has_paid ? "paid" : c.subscription_status] || SUB_META.trial;

  const toggleSuspend = async (c) => {
    if (!c.suspended) { setSuspendTarget(c); setReason(""); return; }
    try {
      await api.post(`/admin/companies/${c.id}/suspend`, { suspended: false });
      toast.success("Société réactivée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmSuspend = async () => {
    try {
      const { data } = await api.post(`/admin/companies/${suspendTarget.id}/suspend`, { suspended: true, reason });
      toast.success(data.email_notified ? "Société suspendue — email envoyé au gérant" : "Société suspendue");
      setSuspendTarget(null); setReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const grantMonths = async () => {
    try {
      const { data } = await api.post(`/admin/companies/${gift.id}/grant-months`, { months: Number(months) });
      toast.success(`${data.months_granted} mois offert(s) — abonnement jusqu'au ${new Date(data.subscription_until).toLocaleDateString("fr-FR")}`);
      setGift(null); setMonths(1); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-companies">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Sociétés</h1>
          <p className="text-muted-foreground text-sm">{items.length} société(s) transporteur inscrites.</p>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une société / email…" className="w-72" data-testid="admin-companies-search" />
      </div>

      <Card className="p-0 rounded-md shadow-none overflow-hidden" data-testid="admin-companies-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Société</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Abonnement</TableHead>
              <TableHead className="text-right">Véhicules</TableHead>
              <TableHead className="text-right">Conducteurs</TableHead>
              <TableHead className="text-right">Courses</TableHead>
              <TableHead className="text-right">MRR</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Aucune société.</TableCell></TableRow>
            )}
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`admin-company-row-${c.id}`} className={c.suspended ? "opacity-60" : ""}>
                <TableCell className="font-medium">
                  {c.company_name}
                  {c.suspended && <Badge variant="destructive" className="ml-2 text-[10px]">Suspendue</Badge>}
                  <span className="block text-xs text-muted-foreground">{c.name}</span>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.email}</TableCell>
                <TableCell><Badge variant="outline" className={sub(c).cls}>{sub(c).label}</Badge></TableCell>
                <TableCell className="text-right text-sm">{c.active_vehicles}<span className="text-muted-foreground">/{c.vehicles}</span></TableCell>
                <TableCell className="text-right text-sm">{c.drivers}</TableCell>
                <TableCell className="text-right text-sm">{c.rides}</TableCell>
                <TableCell className="text-right text-sm font-medium tabular-nums">{(c.mrr_eur || 0).toLocaleString("fr-FR")} €</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" title="Offrir des mois gratuits" onClick={() => { setGift(c); setMonths(1); }} data-testid={`admin-company-gift-${c.id}`}>
                    <Gift className="h-4 w-4 text-primary" />
                  </Button>
                  <Button size="icon" variant="ghost" title={c.suspended ? "Réactiver" : "Suspendre"} onClick={() => toggleSuspend(c)} data-testid={`admin-company-suspend-${c.id}`}>
                    {c.suspended ? <CircleCheck className="h-4 w-4 text-green-600" /> : <Ban className="h-4 w-4 text-destructive" />}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!gift} onOpenChange={(o) => !o && setGift(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Offrir des mois gratuits</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Octroi de mois d'abonnement gratuits à la société.</DialogDescription>
          {gift && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Société : <span className="font-medium text-foreground">{gift.company_name}</span></p>
              <div>
                <Label htmlFor="gift-months">Nombre de mois</Label>
                <Input id="gift-months" type="number" min={1} max={36} value={months} onChange={(e) => setMonths(Number(e.target.value) || 1)} className="mt-1" data-testid="admin-gift-months" />
              </div>
              <p className="text-xs text-muted-foreground">L'abonnement sera prolongé d'autant et passera en statut « Actif ».</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGift(null)}>Annuler</Button>
            <Button onClick={grantMonths} data-testid="admin-gift-confirm">Offrir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!suspendTarget} onOpenChange={(o) => !o && setSuspendTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Suspendre la société</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Suspension d'une société avec motif envoyé par email au gérant.</DialogDescription>
          {suspendTarget && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Société : <span className="font-medium text-foreground">{suspendTarget.company_name}</span></p>
              <div>
                <Label htmlFor="suspend-reason">Motif de suspension</Label>
                <Textarea id="suspend-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex : impayé de la facture de mars, non-respect des CGU…"
                  className="mt-1" rows={3} data-testid="admin-suspend-reason" />
                <p className="text-[11px] text-muted-foreground mt-1">Le gérant recevra un email l'informant de la suspension et du motif. Sa connexion sera bloquée.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendTarget(null)}>Annuler</Button>
            <Button variant="destructive" onClick={confirmSuspend} data-testid="admin-suspend-confirm">Suspendre &amp; notifier</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
